import React, {
    ReactElement,
} from "react";
import {
    CloseSettingsDocument, GetSettingsDocument, RequiredField, TagsSettingsDocument, useSaveSettingsMutation,
} from "../../../../__generated__/api";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import Select from "../../../../components/select/Select";
import TagList from "../../../../components/tag-list/TagList";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../components/theme/settings";
import Toggle from "../../../../components/theme/toggle/Toggle";
import useArticleContext from "../../../../contexts/HelpContext";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import { SettingsProps } from "../base";

const EVERYONE_GROUP = "urn:fq:user-group:everyone";

const TICKET_ASSIGNED = {
    label: "Assigned Agent",
    value: "assigned",
    data: [] as string[],
};
const TICKET_SPECIFIC = {
    label: "Assigned Agent and Specific Groups",
    value: "specific",
    data: [] as string[],
};
const TICKET_EVERYONE = {
    label: "All Agents",
    value: "everyone",
    data: [EVERYONE_GROUP],
};

const OVERRIDE_TICKET_PERMISSIONS = [
    TICKET_ASSIGNED,
    TICKET_EVERYONE,
    TICKET_SPECIFIC,
];

export default function Customization(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();
    useArticleContext("urn:fq:article:e0852e121db14aec98bf149ab1574a38");

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .unsortedArray(
                "tags",
                () => props.settings?.customization?.tags ?? [],
            )
            .unsortedArray(
                "requiredCloseFields",
                () => props.settings?.customization?.requiredCloseFields ?? [],
            )
            .unsortedArray(
                "allowOverrideTicketsGroups",
                () => props.settings?.customization?.allowOverrideTicketsGroups ?? [] as string[] | undefined,
            ),
        [props.settings?.customization],
    );

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: "Settings saved!",
        onSave: (__, onDone) => {
            LoggingHandler.instance.wrap(async () => {
                await save({
                    refetchQueries: [
                        { query: CloseSettingsDocument },
                        { query: TagsSettingsDocument },
                        { query: GetSettingsDocument },
                    ],
                    variables: {
                        customization: {
                            requiredCloseFields: properties.requiredCloseFields.value,
                            tags: properties.tags.value,
                            allowOverrideTicketsGroups: properties.allowOverrideTicketsGroups.value,
                        },
                    },
                });
                onDone();
            });
        },
    });

    const createRequiredFieldHandler = (field: RequiredField, value: boolean) => {
        let copy = Array.from(properties.requiredCloseFields.value ?? []);
        if (value) {
            copy.push(field);
        } else {
            copy = copy.filter(item => item !== field);
        }
        properties.requiredCloseFields.setValue(copy.dedupe());
    };

    let allowTicketGroup = TICKET_ASSIGNED;
    if (properties.allowOverrideTicketsGroups.value?.some(item => item === EVERYONE_GROUP)) {
        allowTicketGroup = TICKET_EVERYONE;
    } else if (properties.allowOverrideTicketsGroups.value != null) {
        allowTicketGroup = TICKET_SPECIFIC;
    }

    return (
        <>
            <SettingsContainer>
                <SettingsContainer.Section
                    title="Required Fields"
                    helpText="These general settings help you manage your sett"
                >
                    <SettingsContainer.Setting
                        title="Require Tags to close"
                        description="Should tags be required to end a conversation?"
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={props.isLoading}
                    >
                        <Toggle
                            checked={properties.requiredCloseFields.value?.includes(RequiredField.Tags) ?? false}
                            size="medium"
                            onChange={event => createRequiredFieldHandler(RequiredField.Tags, event.target.checked)}
                        />
                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Require Comments to close"
                        description="Should ticket comments be required to end a conversation?"
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={props.isLoading}
                    >
                        <Toggle
                            checked={properties.requiredCloseFields.value?.includes(RequiredField.TicketNotes) ?? false}
                            size="medium"
                            onChange={event => createRequiredFieldHandler(RequiredField.TicketNotes, event.target.checked)}
                        />
                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Manage Ticket"
                        description="Who is allowed to manage tickets such as take ownership,
                            send messages, change properties, and close tickets."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={props.isLoading}
                    >
                        <Select
                            className="text-gray mb-2"
                            placeholder=""
                            onChange={value => {
                                if (value === TICKET_ASSIGNED) {
                                    properties.allowOverrideTicketsGroups.setValue(undefined);
                                } else if (value === TICKET_EVERYONE) {
                                    properties.allowOverrideTicketsGroups.setValue([EVERYONE_GROUP]);
                                } else {
                                    properties.allowOverrideTicketsGroups.setValue([]);
                                }
                            }}
                            options={OVERRIDE_TICKET_PERMISSIONS}
                            value={allowTicketGroup}
                        />

                        {allowTicketGroup === TICKET_SPECIFIC && (
                            <FormGroup label="Which groups of users are allowed to edit settings?">
                                <UserGroups
                                    isMulti
                                    minRequired={1}
                                    className="w-100"
                                    onSelected={groupIds => {
                                        properties.allowOverrideTicketsGroups.setValue(groupIds);
                                    }}
                                    value={properties.allowOverrideTicketsGroups.value as string[]}
                                />
                            </FormGroup>
                        )}
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>
                <SettingsContainer.Section
                    title="Other"
                    helpText="The general settings will help you get started with sending emails and receiving emails."
                >

                    <SettingsContainer.Setting
                        title="Ticket Tags"
                        description="These are global tags which agents can add to tickets to help triage
                            tickets, run reports, and store information about the ticket.."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={props.isLoading}
                    >
                        <TagList
                            value={properties.tags.value as string[] ?? []}
                            onChange={value => properties.tags.setValue(value)}
                            isTag
                            canNotHaveDuplicates
                            shouldSort
                        />
                    </SettingsContainer.Setting>
                </SettingsContainer.Section>

            </SettingsContainer>
            {saveElement}
        </>

    );
}
